import { useMemo } from 'react'
import { useMyCohort, type PlatformRole } from '@lb/domain'

import { http } from '../../lib/client'
import { PATH_FOR_SURFACE } from './nav'

const NONE: ReadonlySet<string> = new Set()

/**
 * The nav paths this member cannot open yet — driven by `journey.lockedSurfaces`
 * on `GET /cohorts/mine`, the read the TSP shell already makes.
 *
 * Only TSP asks: the locks are a cohort member's, and every other role has no
 * cohort to be locked by. The day the étape starts the server stops listing
 * the surface, and the next read (focus, or the minute-long staleness) brings
 * the item back — no client clock, and no release.
 *
 * While the read is in flight the lockable items stay hidden, so a locked
 * member is never shown a door that then vanishes; a failed read hides
 * nothing, and the route's own `SURFACE_LOCKED` answer still holds the line.
 */
export function useHiddenNavPaths(role: PlatformRole): ReadonlySet<string> {
  const tsp = role === 'TSP'
  const cohort = useMyCohort(http, { enabled: tsp })

  return useMemo(() => {
    if (!tsp) return NONE
    if (cohort.isPending) return new Set(Object.values(PATH_FOR_SURFACE))
    const locks = cohort.data?.lockedSurfaces ?? []
    const paths = locks
      .map((lock) => PATH_FOR_SURFACE[lock.surface])
      .filter((path): path is string => path !== undefined)
    return paths.length > 0 ? new Set(paths) : NONE
  }, [tsp, cohort.isPending, cohort.data])
}
